import {
    environmentQueryKey,
    useEnvironment,
    useStartEnvironment,
    type PlainEnvironment,
} from "@/queries/environment-queries";
import { showStart, stateFromEnvironment, type EnvironmentState } from "@/routes/environments/phase";
import { useQueryClient } from "@tanstack/react-query";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useRef, useState } from "react";

type UseAutoStartEnvironmentOptions = {
    skipPolicyCheck?: boolean;
};

type UseAutoStartEnvironmentResult = {
    /** Starts the environment if it's in a startable state. No-op if already running or data hasn't loaded. */
    ensureEnvironmentStarted: () => Promise<void>;
    /** True from when ensureEnvironmentStarted() begins until the environment reaches RUNNING (or the attempt fails). */
    isStarting: boolean;
    /** Error from the last start attempt, if any. */
    error: Error | null;
};

/**
 * Max time (ms) to wait for the environment to reach RUNNING after a start
 * request. 60 s covers typical startup (VM provision + devcontainer build).
 * If exceeded, ensureEnvironmentStarted resolves anyway so the caller's
 * follow-up RPC can surface its own error if the env still isn't ready.
 */
const WAIT_FOR_RUNNING_TIMEOUT_MS = 60_000;

/**
 * Low-level hook for user-initiated actions that need the environment running
 * (e.g. "Run task", "Toggle service"). Call `ensureEnvironmentStarted()` before
 * the action's RPC — it starts the env if stopped and waits for RUNNING phase.
 *
 * For pages/views that should auto-start on navigation (log viewers, code tab),
 * use {@link useEnvironmentStartingState} instead.
 */
export function useAutoStartEnvironment(
    environmentId: string,
    options?: UseAutoStartEnvironmentOptions,
): UseAutoStartEnvironmentResult {
    const startEnvironment = useStartEnvironment();
    const queryClient = useQueryClient();
    const startingRef = useRef(false);
    const [isStarting, setIsStarting] = useState(false);

    const ensureEnvironmentStarted = useCallback(async () => {
        // Read the environment from the cache at call time so the callback
        // identity doesn't change on every environment status update.
        const environment = queryClient.getQueryData<PlainEnvironment>(
            environmentQueryKey.get(environmentId),
        );
        if (!environment || !showStart(environment)) {
            return;
        }

        // Prevent concurrent start attempts
        if (startingRef.current) {
            return;
        }

        startingRef.current = true;
        setIsStarting(true);
        try {
            await startEnvironment.mutateAsync({
                environmentId,
                skipPolicyCheck: options?.skipPolicyCheck,
            });

            // Wait for the environment to actually reach RUNNING in the cache
            // before resolving, so callers can safely issue follow-up RPCs.
            await new Promise<void>((resolve) => {
                const timeout = setTimeout(() => {
                    unsubscribe();
                    // Resolve anyway — the caller's RPC will surface its own error
                    // if the environment still isn't running.
                    resolve();
                }, WAIT_FOR_RUNNING_TIMEOUT_MS);

                const checkRunning = () => {
                    const cached = queryClient.getQueryData<PlainEnvironment>(
                        environmentQueryKey.get(environmentId),
                    );
                    return cached?.status?.phase === EnvironmentPhase.RUNNING;
                };

                // Already running (e.g. cache updated during the mutation)
                if (checkRunning()) {
                    clearTimeout(timeout);
                    resolve();
                    return;
                }

                const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
                    if (event.type !== "updated") {
                        return;
                    }
                    if (checkRunning()) {
                        clearTimeout(timeout);
                        unsubscribe();
                        resolve();
                    }
                });
            });
        } finally {
            startingRef.current = false;
            setIsStarting(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only startEnvironment.mutateAsync is needed; including the full object causes re-renders on every mutation state change
    }, [environmentId, startEnvironment.mutateAsync, queryClient, options?.skipPolicyCheck]);

    return {
        ensureEnvironmentStarted,
        isStarting,
        error: startEnvironment.error,
    };
}

type UseEnvironmentStartingStateResult = {
    environment: PlainEnvironment | undefined;
    ensureEnvironmentStarted: () => Promise<void>;
    isStarting: boolean;
    envState: EnvironmentState | undefined;
    /** True when the EnvironmentStartingState should be shown instead of content. */
    showStartingState: boolean;
};

/**
 * Derives environment starting-state for views that show
 * {@link EnvironmentStartingState} when the env isn't running (log viewers,
 * code tab). Pure state derivation — does NOT auto-start the environment.
 *
 * Auto-starting is handled by the action handlers that trigger navigation
 * to these views (e.g. service toggle, task run button).
 */
export function useEnvironmentStartingState(environmentId: string): UseEnvironmentStartingStateResult {
    const { data: environment } = useEnvironment(environmentId);
    const { ensureEnvironmentStarted, isStarting } = useAutoStartEnvironment(environmentId);

    const envState = environment ? stateFromEnvironment(environment) : undefined;
    // Show the starting/stopped overlay whenever the env isn't running.
    // This prevents a flash of "Failed to fetch" logs while the env boots.
    const showStartingState = !!environment && envState !== "running";

    return { environment, ensureEnvironmentStarted, isStarting, envState, showStartingState };
}
